import { GET, PATCH, POST } from '../../service/HttpMethod';
import { PublishPreview } from './types';
import { InvitationDispatchResult } from './invitationDelivery';

export interface PublishProjectOptions {
	aiDisclaimer: string;
	includeGroupingBulkAnswers: boolean;
	includeScopeLocations: boolean;
	includeQuoteReview: boolean;
}

export interface PublishProjectResult extends InvitationDispatchResult {
	published: boolean;
	publishedAt?: string | null;
	republishRequired?: boolean;
	revisionActionId?: number | null;
	revisionActionType?: 'SCAN_CURRENT' | 'MAKE_CURRENT' | null;
	revisionDocumentVersion?: string | null;
}

export interface BidListAuditSuggestion {
	bidPackageId: number;
	bidPackageName: string;
	companyName?: string;
	reason: string;
	sources?: Array<{ title: string; url: string }>;
	suggestedContacts?: Array<{
		email?: string | null;
		fullName?: string | null;
		phone?: string | null;
	}>;
	suggestionId?: string;
	vendorCompanyId?: number | null;
	vendorCompanyName: string;
	website?: string | null;
}

export interface BidListAuditResult {
	runId?: number;
	responseId: string | null;
	status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
	suggestions: BidListAuditSuggestion[];
	errorMessage?: string | null;
	presentedAt?: string | null;
	requirementCoverage?: {
		current: Array<{
			achievedPercentage: number;
			matchingCompanyCount: number;
			minimumPercentage: number;
			requiredCompanyCount: number;
			requirementId: string;
			shortfallCount: number;
			type: 'ENTERPRISE_TYPE' | 'LABOR_TYPE';
			values: string[];
		}>;
		projected: Array<{
			achievedPercentage: number;
			matchingCompanyCount: number;
			minimumPercentage: number;
			requiredCompanyCount: number;
			requirementId: string;
			shortfallCount: number;
			type: 'ENTERPRISE_TYPE' | 'LABOR_TYPE';
			values: string[];
		}>;
	};
}

export interface BidListAuditSelection {
	bidPackageId: number;
	suggestionId?: string;
	vendorCompanyId?: number | null;
}

const unwrap = <T>(response: any): T => {
	const body = response && response.data !== undefined ? response.data : response;
	return (body && body.data !== undefined ? body.data : body) as T;
};

export const startBidListAudit = async (projectId: number, auditInstructions = '') =>
	unwrap<BidListAuditResult>(
		await POST({
			url: `projects/${projectId}/bid-invites/audit`,
			data: { auditInstructions: auditInstructions.trim() },
		}),
	);

export const getBidListAuditResult = async (
	projectId: number,
	runIdOrResponseId: number | string,
) =>
	unwrap<BidListAuditResult>(
		await GET({
			url: `projects/${projectId}/bid-invites/audit/${encodeURIComponent(
				String(runIdOrResponseId),
			)}`,
		}),
	);

export const getLatestBidListAudit = async (projectId: number) =>
	unwrap<BidListAuditResult | null>(
		await GET({ url: `projects/${projectId}/bid-invites/audit/latest` }),
	);

export const markBidListAuditPresented = async (projectId: number, runId: number) =>
	unwrap<BidListAuditResult>(
		await PATCH({
			url: `projects/${projectId}/bid-invites/audit/${runId}/presented`,
			data: {},
		}),
	);

export const applyBidListAuditSelections = async (
	projectId: number,
	responseId: string,
	selections: BidListAuditSelection[],
) =>
	unwrap<{ addedCount: number; selectedCount: number }>(
		await POST({
			url: `projects/${projectId}/bid-invites/audit/${encodeURIComponent(responseId)}/apply`,
			data: { selections },
		}),
	);

export const getPublishPreview = async (projectId: number) =>
	unwrap<PublishPreview>(await GET({ url: `projects/${projectId}/bid-invites/publish-preview` }));

export const publishProject = async (projectId: number, options: PublishProjectOptions) =>
	unwrap<PublishProjectResult>(
		await POST({
			url: `projects/${projectId}/bid-invites/publish`,
			data: options,
		}),
	);
